<script lang="ts">
  import {
    lawyerDetailStore,
    firmLawyersStore,
    patchLawyer,
  } from "@entities/lawyer/store.svelte";
  import { affiliatedFirmStore } from "@entities/firm/store.svelte";
  import { productOptionsStore } from "@entities/product/store.svelte";
  import { assignableAccountsStore } from "@entities/account/store.svelte";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import { timelineStore } from "@entities/timeline/store.svelte";
  import { contractListStore } from "@entities/contract/store.svelte";
  import { salesActionListStore } from "@entities/sales-action/store.svelte";
  import LawyerDetail from "@widgets/lawyer-detail/LawyerDetail.svelte";
  import Timeline from "@widgets/timeline/Timeline.svelte";
  import ContractTable from "@widgets/contract-table/ContractTable.svelte";
  import SalesActionPanel from "@widgets/sales-action-panel/SalesActionPanel.svelte";
  import CustomerMemoPanel from "@widgets/customer-memo-panel/CustomerMemoPanel.svelte";
  import ContractLineItemsEditor from "@widgets/contract-line-items-editor/ContractLineItemsEditor.svelte";
  import AddDialogueLogModal from "@features/dialogue-log-create/AddDialogueLogModal.svelte";
  import ContractCreateModal from "@features/contract-create/ContractCreateModal.svelte";
  import { contractLinkFlow } from "@features/contract-create/contract-link.svelte";
  import ContractTransitionModal from "@features/contract-transition/ContractTransitionModal.svelte";
  import ContractArchiveModal from "@features/contract-archive/ContractArchiveModal.svelte";
  import ContractRestoreModal from "@features/contract-restore/ContractRestoreModal.svelte";
  import AccountSelect from "@shared/ui/AccountSelect.svelte";
  import { listFormSubmissions } from "@shared/api/form-submissions";
  import { formCrmEnabled } from "@shared/config/features";
  import { listOpportunities } from "@shared/api/opportunities";
  import { parsePaged } from "@shared/api/paging";
  import type { Contract } from "@domain/contract/model";
  import type { Opportunity } from "@domain/opportunity/model";
  import { parseOpportunity } from "@domain/opportunity/parseOpportunity";
  import type { TimelineQuery } from "@domain/timeline/model";
  import type { DialogueLog } from "@shared/types/dialogue-log";
  import type { FormSubmission } from "@shared/types/form-submission";
  import { canEdit } from "@shared/types/session";
  import {
    hasFirmCompetitorCaution,
    hasSalesCaution,
    isFirmCompetitorReasonMissing,
    reasonOrUnset,
  } from "@domain/lawyer/sales-caution";
  import { lawyerBulkSalesSummaryLabel } from "@domain/bulk-sales/labels";
  import { disciplinaryContactCautionBadgeLabel } from "@domain/lawyer/disciplinary-sales-signal";
  import { affiliatedFirmLabel } from "@domain/firm/affiliated-firm-label";

  interface Props {
    id: string;
  }

  let { id }: Props = $props();

  type TabKey = "profile" | "sales" | "deals" | "memos";
  const COMPETITOR_USAGE_HASH = "#competitor-solution-usage";

  function hasCompetitorUsageHash(): boolean {
    return typeof window !== "undefined" && window.location.hash === COMPETITOR_USAGE_HASH;
  }

  function initialTab(): TabKey {
    return hasCompetitorUsageHash() ? "profile" : "sales";
  }

  function scrollToCompetitorUsage(): void {
    if (!hasCompetitorUsageHash() || typeof document === "undefined") return;
    window.setTimeout(() => {
      document.getElementById("competitor-solution-usage")?.scrollIntoView({ block: "start" });
    }, 0);
  }

  // 営業担当が最初に営業アクションを確認できるよう、初期表示は sales タブ。
  let activeTab = $state<TabKey>(initialTab());
  let dialogCreateOpen = $state(false);
  // 手動タイムラインの編集対象 (entry カードの編集ボタンから設定)。
  let entryEditTarget = $state<DialogueLog | null>(null);
  let contractCreateOpen = $state(false);
  let contractEditTarget = $state<Contract | null>(null);
  let contractTransitionTarget = $state<Contract | null>(null);
  // 契約タブの表示切替: 通常契約 (アーカイブ除外) / アーカイブ済み。
  let contractView = $state<"normal" | "archived">("normal");
  let contractArchiveTarget = $state<Contract | null>(null);
  let contractRestoreTarget = $state<Contract | null>(null);
  // 所属事務所内の弁護士 / 所属事務所 / 商品マスタ / 担当者候補は entity store が
  // 取得・状態保持する。コンポーネントは store を参照して表示するだけ (#431)。
  let firmLawyers = $derived(firmLawyersStore.data);
  let affiliatedFirm = $derived(affiliatedFirmStore.data);
  let affiliatedFirmLoading = $derived(affiliatedFirmStore.loading);
  let affiliatedFirmError = $derived(affiliatedFirmStore.error);
  let salesSolutionsError = $derived(productOptionsStore.error);
  // 所属事務所の商談 (タイムラインモーダルの関連商談プルダウン用)。
  let opportunities = $state<Opportunity[]>([]);
  // 取得済み/取得中の対象 firm_id。 別 firm の弁護士へ遷移した場合に前の事務所の
  // 候補を出さないよう、 firm 単位で再取得・競合ガードする。
  let opportunitiesFirmId = $state<string | null>(null);
  // 担当者マスタ (ACC-4)。契約一覧の担当者列の表示名解決用。
  let accounts = $derived(assignableAccountsStore.data);
  let salesSolutionOptions = $derived(
    productOptionsStore.data.map((p) => ({ id: p.id, label: p.name })),
  );
  // 契約対象弁護士 (main_interest_lawyer_id) 等は基本的に所属事務所内の弁護士。
  // firmLawyers が既に load されているのでそれを options 化する。
  let lawyerOptions = $derived(
    firmLawyers.map((l) => ({
      id: l.id,
      label: l.full_name,
    })),
  );
  // タイムライン参加者行の id → 名前解決。
  let lawyerNameById = $derived(
    Object.fromEntries(firmLawyers.map((l) => [l.id, l.full_name])),
  );
  // タイムラインの商談/契約カード詳細表示「操作者」の id → 名前解決。
  let accountNameById = $derived(
    Object.fromEntries(accounts.map((a) => [a.id, a.display_name])),
  );
  let proposalOptions = $derived(
    opportunities.map((o) => ({ id: o.id, name: o.name })),
  );

  let allowEdit = $derived(
    sessionStore.user ? canEdit(sessionStore.user.role) : false,
  );
  let firmId = $derived(lawyerDetailStore.data?.firm_id ?? null);
  // company 所属 (インハウス) では firm アンカーの機能 (営業アクション/契約/メモ/
  // タイムライン追加/所属 select) を出さない (epic-master-renewal 判断 7)。
  // 「見えるが作成時に失敗する」状態を作らないため、タブごと非表示にして
  // 表示タブを profile に固定する。
  let isFirmAffiliated = $derived(lawyerDetailStore.data?.firm_id != null);
  let effectiveTab = $derived<TabKey>(isFirmAffiliated ? activeTab : "profile");
  let pendingFormSubmissions = $state<FormSubmission[]>([]);
  let pendingFormSubmissionTargetId = $state<string | null>(null);
  let pendingFormSubmissionError = $state<string | null>(null);

  // 担当者 (assignee)。弁護士レコード全体の所有者なので、基本情報ではなくヘッダー
  // 右上で表示・編集する。保存先は既存どおり lawyers.assignee_account_id。
  //
  // 候補は顧客DB用の read-only endpoint から取得する。管理用 `/api/admin/accounts`
  // は使わず、member も担当者変更できる既存仕様を維持する。
  let assigneeAccountId = $state(lawyerDetailStore.data?.assignee_account_id ?? "");
  let assigneeSaving = $state(false);
  let assigneeError = $state<string | null>(null);
  $effect(() => {
    assigneeAccountId = lawyerDetailStore.data?.assignee_account_id ?? "";
  });
  function assigneeDisplayName(): string {
    const aid = lawyerDetailStore.data?.assignee_account_id;
    if (!aid) return "(未割当)";
    // 取得失敗や候補外 ID の場合でも「(未割当)」とは出さず、設定済みであることは示す。
    return accounts.find((a) => a.id === aid)?.display_name ?? "(担当者あり)";
  }
  async function saveAssignee(next: string) {
    const target = lawyerDetailStore.data;
    if (!target || assigneeSaving) return;
    assigneeSaving = true;
    assigneeError = null;
    try {
      await patchLawyer(target.id, {
        assignee_account_id: next === "" ? null : next,
      });
    } catch (err) {
      // 保存失敗時は選択を保存前の値へ戻し、変更できたように見えるのを防ぐ。
      assigneeAccountId = target.assignee_account_id ?? "";
      assigneeError = err instanceof Error ? err.message : String(err);
    } finally {
      assigneeSaving = false;
    }
  }

  $effect(() => {
    void lawyerDetailStore.load(id);
    return () => {
      lawyerDetailStore.clear();
      timelineStore.clear();
      firmLawyersStore.clear();
      affiliatedFirmStore.clear();
      productOptionsStore.clear();
      assignableAccountsStore.clear();
      // 前ターゲットの営業アクション行を次の画面へ持ち越さない (FirmDetailPage と
      // 対称)。 store.refresh は data を先に破棄しないため、 clear しないと遷移直後に
      // 前の弁護士/事務所の行が一瞬表示される (read 統一で行集合が広がり悪化)。
      salesActionListStore.clear();
      pendingFormSubmissions = [];
      pendingFormSubmissionTargetId = null;
      pendingFormSubmissionError = null;
      // モーダルは前の弁護士の contract/entry を握ったまま id 遷移し得る
      // (App.svelte はコンポーネントを {#key} なしで再利用する) ため、開閉と
      // 対象をここでまとめて破棄する。
      dialogCreateOpen = false;
      entryEditTarget = null;
      contractCreateOpen = false;
      contractEditTarget = null;
      contractTransitionTarget = null;
      contractArchiveTarget = null;
      contractRestoreTarget = null;
      // 契約作成 → 紐付けフローは firm/lawyer 両ページで共有するシングルトン
      // (features/contract-create/contract-link.svelte.ts)。 前弁護士の作成元
      // アクション・再試行状態を次の画面へ持ち越さない。
      contractLinkFlow.reset();
    };
  });

  // 所属事務所内の弁護士 (firmLawyers) と所属事務所名 (affiliatedFirm) は
  // タブに依存せず lawyer 詳細が load された時点で取得する。 初期表示が営業
  // アクションタブのため、 profile 依存だと SalesActionPanel の対象弁護士名が
  // ID 表示/空候補になる (firmLawyers は名前解決・選択肢・モーダルに使う)。
  // affiliatedFirm はヘッダーで常時リンク表示するため同様。
  $effect(() => {
    if (lawyerDetailStore.data?.id === id) {
      const fid = lawyerDetailStore.data.firm_id ?? null;
      void firmLawyersStore.load(fid);
      void affiliatedFirmStore.load(fid);
      void loadOpportunities(fid);
    }
  });

  $effect(() => {
    if (lawyerDetailStore.data?.id === id && hasCompetitorUsageHash()) {
      activeTab = "profile";
    }
  });

  // 接点履歴 (Timeline) は profile タブの右カラムでのみ描画するため、 profile を
  // 開いた時に取得する。
  // 外部レビュー #1: 弁護士詳細は所属 firm ではなく、 その弁護士が participant に
  // 含まれるエントリ (= lawyer_id) でタイムラインを読む。
  $effect(() => {
    if (effectiveTab === "profile" && lawyerDetailStore.data?.id === id) {
      void timelineStore.loadLawyer(id);
      scrollToCompetitorUsage();
    }
  });

  // catalog No.113/114: 弁護士単位の契約一覧。
  // main_interest が該当弁護士の契約のみを表示する。
  $effect(() => {
    if (effectiveTab === "deals" && lawyerDetailStore.data?.id === id) {
      void contractListStore.refresh({
        lawyer_id: id,
        archive_filter: contractView === "archived" ? "only" : "exclude",
        page: 1,
        page_size: 200,
        sort_by: "updated_at",
        sort_dir: "desc",
      });
    }
  });

  // 商品マスタは契約一覧 (deals タブ) と営業アクション (sales タブ) の
  // 両方で product_id → name 解決に使うので、 lawyer 詳細を開いた時点で 1 回
  // load しておく。 タブを開いてから fetch すると 「ID のまま見える時間」 が
  // 発生して旧 UI の挙動と乖離するため。
  $effect(() => {
    if (
      lawyerDetailStore.data?.id === id &&
      productOptionsStore.data.length === 0
    ) {
      void productOptionsStore.load();
    }
  });

  $effect(() => {
    if (lawyerDetailStore.data?.id === id && !assignableAccountsStore.loaded) {
      void assignableAccountsStore.load();
    }
  });

  $effect(() => {
    if (!formCrmEnabled) return;
    if (
      lawyerDetailStore.data?.id === id &&
      pendingFormSubmissionTargetId !== id
    ) {
      pendingFormSubmissionTargetId = id;
      pendingFormSubmissions = [];
      pendingFormSubmissionError = null;
      void loadPendingFormSubmissionsForLawyer(id);
    }
  });

  async function loadPendingFormSubmissionsForLawyer(
    lawyerId: string,
  ): Promise<void> {
    if (!formCrmEnabled) return;
    try {
      const res = await listFormSubmissions({
        matched_lawyer_id: lawyerId,
        field_update_status: "pending",
        limit: 5,
      });
      if (lawyerId !== id || lawyerDetailStore.data?.id !== lawyerId) return;
      pendingFormSubmissions = res.items;
      pendingFormSubmissionError = null;
    } catch (err) {
      if (lawyerId !== id) return;
      pendingFormSubmissionError = err instanceof Error ? err.message : String(err);
    }
  }

  function pendingFormSubmissionCountLabel(): string {
    return pendingFormSubmissions.length >= 5
      ? `${pendingFormSubmissions.length}件以上`
      : `${pendingFormSubmissions.length}件`;
  }

  function formSubmissionReceivedLabel(submission: FormSubmission): string {
    return submission.received_at.slice(0, 16).replace("T", " ");
  }

  async function loadOpportunities(fid: string | null) {
    // 同じ firm を取得済み/取得中なら再取得しない。
    if (opportunitiesFirmId === fid) return;
    opportunitiesFirmId = fid;
    opportunities = [];
    if (!fid) return;
    try {
      const page = parsePaged(
        await listOpportunities({
          firm_id: fid,
          page: 1,
          page_size: 200,
          sort_by: "created_at",
          sort_dir: "desc",
        }),
        parseOpportunity,
      );
      // 別 firm の弁護士へ遷移済みなら古いレスポンスを捨てる。
      if (opportunitiesFirmId === fid) opportunities = page.items;
    } catch {
      // 関連商談プルダウンは付加情報。失敗してもタイムライン自体は使える。
      if (opportunitiesFirmId === fid) opportunities = [];
    }
  }

  async function refreshTimeline() {
    await timelineStore.loadLawyer(id, timelineStore.query);
  }

  function handleTimelineFilterChange(query: TimelineQuery): void {
    void timelineStore.loadLawyer(id, query);
  }

  function handleTimelineLoadMore(): void {
    void timelineStore.loadMore();
  }

  function refreshContracts() {
    void contractListStore.refresh({
      lawyer_id: id,
      archive_filter: contractView === "archived" ? "only" : "exclude",
      page: 1,
      page_size: 200,
      sort_by: "updated_at",
      sort_dir: "desc",
    });
  }

  // 契約作成成功後、 作成元の受注アクション (結果タブの「契約を作成する」CTA から
  // 開いた場合) があれば linked_contract_id を紐付ける (共通化フロー /
  // features/contract-create/contract-link.svelte.ts)。 手動の「+ 契約を追加」では
  // 作成元が無いため紐付けは行わない。 別弁護士へ遷移済みなら (isStale) 状態更新を
  // 行わない (FirmDetailPage と対称)。
  function handleContractCreated(contract: Contract) {
    contractCreateOpen = false;
    refreshContracts();
    void refreshTimeline();
    const lawyerId = id;
    void contractLinkFlow.handleCreated(contract, undefined, () => lawyerId !== id);
  }

  function retryContractLink() {
    const lawyerId = id;
    void contractLinkFlow.retryLink(undefined, () => lawyerId !== id);
  }

  function handleContractUpdated() {
    contractEditTarget = null;
    refreshContracts();
    void refreshTimeline();
  }

  function handleContractTransitioned() {
    contractTransitionTarget = null;
    refreshContracts();
    void refreshTimeline();
  }

  function handleContractArchived() {
    contractArchiveTarget = null;
    refreshContracts();
    void refreshTimeline();
  }

  function handleContractRestored() {
    contractRestoreTarget = null;
    refreshContracts();
    void refreshTimeline();
  }

  function handleEntryCreated() {
    dialogCreateOpen = false;
    void refreshTimeline();
  }

  // 編集保存成功時: モーダルを閉じてタイムラインを再取得する。
  // Timeline コンポーネントは再マウントされないため、フィルタ状態は維持される。
  function handleEntryUpdated() {
    entryEditTarget = null;
    void refreshTimeline();
  }
</script>

<p class="back-link"><a href="/lawyers">← 弁護士一覧へ</a></p>

{#if lawyerDetailStore.loading && lawyerDetailStore.data === null}
  <p class="status">読み込み中…</p>
{:else if lawyerDetailStore.error}
  <p class="status error">{lawyerDetailStore.error}</p>
{:else if lawyerDetailStore.data}
  {@const lawyer = lawyerDetailStore.data}
  {@const disciplinaryCautionLabel = disciplinaryContactCautionBadgeLabel(lawyer)}
  {@const bulkSalesLabel = lawyerBulkSalesSummaryLabel(lawyer.bulk_sales_decision)}
  <header class="page-header">
    <div class="header-main">
      <h1>{lawyer.full_name}</h1>
      <p class="meta">
        {#if lawyer.full_name_kana}
          <span>{lawyer.full_name_kana}</span>
        {/if}
        {#if lawyer.firm_id !== null}
          <!-- 保存値 firm_id (UUID) は主表示にせず、表示ラベルは firms.official_name。
               取得中・取得失敗でも UUID を出さず短い文言を出す (#571)。リンク先は
               引き続き /firms/{firm_id}。 -->
          <span>
            所属:&nbsp;<a href={`/firms/${lawyer.firm_id}`}
              >{affiliatedFirmLabel(
                affiliatedFirm,
                affiliatedFirmLoading,
                Boolean(affiliatedFirmError),
              )}</a
            >
          </span>
        {:else}
          <!-- company 所属 (インハウス): 企業名を read-only テキスト表示 (リンクなし)。 -->
          <span data-testid="lawyer-header-company">
            所属:&nbsp;{lawyer.company_name ?? "-"}
          </span>
        {/if}
      </p>
      {#if hasSalesCaution(lawyer, affiliatedFirm) || disciplinaryCautionLabel !== null}
        <div class="sales-caution-badges" aria-label="営業対象外・競合・懲戒の注意表示">
          {#if bulkSalesLabel}
            <!-- 一括営業可否は backend の派生値 (本人 / 事務所 / 懲戒の OR 合成) を描画する。
                 旧営業NG理由の欠落は「!」で示さない (read-only の遺産表示のみ)。 -->
            <span
              class="sales-caution-badge sales-caution-badge-excluded"
              title={bulkSalesLabel}
              data-testid="lawyer-header-bulk-sales-badge"
            >
              {bulkSalesLabel}
            </span>
          {/if}
          {#if hasFirmCompetitorCaution(affiliatedFirm)}
            <span
              class="sales-caution-badge sales-caution-badge-competitor"
              title={isFirmCompetitorReasonMissing(affiliatedFirm)
                ? "競合（事務所）: 理由未設定"
                : "競合（事務所）"}
            >
              競合（事務所）
              {#if isFirmCompetitorReasonMissing(affiliatedFirm)}
                <span class="reason-missing" aria-label="理由未設定">!</span>
              {/if}
            </span>
          {/if}
          {#if disciplinaryCautionLabel}
            <span
              class="sales-caution-badge"
              class:sales-caution-badge-disciplinary-review={lawyer.disciplinary_sales_handling ===
                "needs_review"}
              class:sales-caution-badge-disciplinary-stop={lawyer.disciplinary_sales_handling ===
                "stop_contact"}
            >
              {disciplinaryCautionLabel}
            </span>
          {/if}
        </div>
      {/if}
      {#if hasFirmCompetitorCaution(affiliatedFirm)}
        <div class="sales-caution-alerts" aria-label="競合の理由">
          <p>競合理由: {reasonOrUnset(affiliatedFirm?.competitor_reason)}</p>
        </div>
      {/if}
    </div>
    <div class="title-right">
      <div class="assignee" data-testid="lawyer-header-assignee">
        <span class="assignee-label">担当者:</span>
        {#if allowEdit}
          <AccountSelect
            bind:value={assigneeAccountId}
            accounts={accounts.length > 0 ? accounts : null}
            disabled={assigneeSaving}
            placeholder="(未割当)"
            onChange={(next) => void saveAssignee(next)}
          />
          {#if assigneeSaving}<span class="assignee-saving">保存中…</span>{/if}
          {#if assigneeError}<span class="assignee-error" role="alert"
              >{assigneeError}</span
            >{/if}
        {:else}
          <span class="assignee-value">{assigneeDisplayName()}</span>
        {/if}
      </div>
      <div class="meta-update">
        更新: {lawyer.updated_at ? lawyer.updated_at.slice(0, 10) : "-"}
      </div>
    </div>
  </header>

  {#if pendingFormSubmissions.length > 0 || pendingFormSubmissionError}
    <section class="pending-form-alert" aria-label="要承認のフォーム送信">
      <div class="pending-form-alert-head">
        <span>要承認のフォーム送信</span>
        {#if pendingFormSubmissions.length > 0}
          <strong>{pendingFormSubmissionCountLabel()}</strong>
        {/if}
      </div>
      {#if pendingFormSubmissionError}
        <p class="pending-form-error">{pendingFormSubmissionError}</p>
      {:else}
        <div class="pending-form-links">
          {#each pendingFormSubmissions as submission (submission.id)}
            <a href={`/form-submissions/${submission.id}`}>
              {formSubmissionReceivedLabel(submission)}
            </a>
          {/each}
        </div>
      {/if}
    </section>
  {/if}

  <nav class="tabs" aria-label="詳細タブ">
    {#if isFirmAffiliated}
      <button
        type="button"
        class:active={effectiveTab === "sales"}
        data-testid="lawyer-tab-sales-action"
        onclick={() => (activeTab = "sales")}
      >
        営業アクション
      </button>
    {/if}
    <button
      type="button"
      class:active={effectiveTab === "profile"}
      onclick={() => (activeTab = "profile")}
    >
      項目情報・タイムライン
    </button>
    {#if isFirmAffiliated}
      <button
        type="button"
        class:active={effectiveTab === "deals"}
        onclick={() => (activeTab = "deals")}
      >
        契約
      </button>
      <button
        type="button"
        class:active={effectiveTab === "memos"}
        data-testid="lawyer-tab-memos"
        onclick={() => (activeTab = "memos")}
      >
        メモ
      </button>
    {/if}
  </nav>

  {#if effectiveTab === "profile"}
    <div class="detail-profile-grid">
      <section class="profile-summary" aria-label="項目情報">
        <LawyerDetail
          {lawyer}
          canEdit={allowEdit}
          {affiliatedFirm}
          {affiliatedFirmError}
        />
      </section>
      <section class="profile-timeline" aria-label="タイムライン">
        <header class="timeline-header">
          <h3>タイムライン</h3>
          {#if isFirmAffiliated}
            <!-- タイムライン参加は複合 FK (lawyer_id, firm_id) の firm アンカー機能。
                 company 所属では追加導線ごと出さない (閲覧は残す)。 -->
            <div class="timeline-actions">
              <button
                type="button"
                disabled={!allowEdit}
                onclick={() => (dialogCreateOpen = true)}
              >
                + タイムラインに追加
              </button>
            </div>
          {/if}
        </header>
        <Timeline
          items={timelineStore.items}
          counts={timelineStore.counts}
          loading={timelineStore.loading}
          error={timelineStore.error}
          {lawyerNameById}
          {accountNameById}
          viewerLawyerId={lawyer.id}
          onFilterChange={handleTimelineFilterChange}
          onLoadMore={handleTimelineLoadMore}
          canLoadMore={timelineStore.canLoadMore}
          onEditEntry={allowEdit && isFirmAffiliated
            ? (entry) => (entryEditTarget = entry)
            : undefined}
        />
      </section>
    </div>
  {:else if effectiveTab === "sales"}
    <section class="sales-action-stack" aria-label="提案・アクション一覧">
      {#if salesSolutionsError}<p class="status error">
          {salesSolutionsError}
        </p>{/if}
      <SalesActionPanel
        target={{ kind: "lawyer", id: lawyer.id }}
        canEdit={allowEdit}
        solutions={salesSolutionOptions}
        lawyers={firmLawyers}
        onCreateContract={(action) => {
          if (!firmId) return;
          contractLinkFlow.startFrom(action);
          activeTab = "deals";
          contractCreateOpen = true;
        }}
        resultCreateContractCta
      />
    </section>
  {:else if effectiveTab === "deals"}
    <section class="tab-body" aria-label="契約">
      <header class="tab-header">
        <h3>契約</h3>
        {#if contractView === "normal"}
          <button
            type="button"
            class="primary"
            disabled={!allowEdit || !firmId}
            title={firmId ? undefined : "事務所未設定のため契約を追加できません"}
            onclick={() => {
              contractLinkFlow.clearSource();
              contractCreateOpen = true;
            }}
          >
            + 契約を追加
          </button>
        {/if}
      </header>
      <div class="contract-view-toggle" role="group" aria-label="契約表示の切替">
        <button
          type="button"
          class:active={contractView === "normal"}
          onclick={() => (contractView = "normal")}
        >
          通常契約
        </button>
        <button
          type="button"
          class:active={contractView === "archived"}
          onclick={() => (contractView = "archived")}
        >
          アーカイブ済み
        </button>
      </div>
      {#if contractView === "normal" && contractLinkFlow.error}
        <div class="link-error" data-testid="lawyer-contract-link-error">
          <p class="status error">
            契約は作成されましたが、営業アクションへの紐付けに失敗しました:
            {contractLinkFlow.error}
          </p>
          {#if contractLinkFlow.retry}
            <button
              type="button"
              data-testid="contract-link-retry"
              onclick={retryContractLink}
            >
              紐付けを再試行
            </button>
          {/if}
        </div>
      {/if}
      {#if contractListStore.loading && contractListStore.data === null}
        <p class="status">読み込み中…</p>
      {:else if contractListStore.error}
        <p class="status error">{contractListStore.error}</p>
      {:else if contractListStore.data}
        <ContractTable
          contracts={contractListStore.data.items}
          sortBy="updated_at"
          sortDir="desc"
          onSort={() => {}}
          hideFirm
          canEdit={allowEdit}
          archived={contractView === "archived"}
          {lawyerOptions}
          accountOptions={accounts}
          onEdit={contractView === "archived"
            ? undefined
            : (c) => (contractEditTarget = c)}
          onTransition={contractView === "archived"
            ? undefined
            : (c) => (contractTransitionTarget = c)}
          onArchive={contractView === "archived"
            ? undefined
            : (c) => (contractArchiveTarget = c)}
          onRestore={contractView === "archived"
            ? (c) => (contractRestoreTarget = c)
            : undefined}
        />
      {/if}
    </section>
  {:else if effectiveTab === "memos"}
    <section class="tab-body" aria-label="メモ">
      <CustomerMemoPanel
        target={{ kind: "lawyer", id: lawyer.id }}
        canEdit={allowEdit}
      />
    </section>
  {/if}

  {#if firmId}
    <AddDialogueLogModal
      open={dialogCreateOpen}
      {firmId}
      lawyers={firmLawyers}
      proposals={proposalOptions}
      defaultLawyerId={lawyer.id}
      onClose={() => (dialogCreateOpen = false)}
      onCreated={handleEntryCreated}
    />
  {/if}
  {#if firmId && entryEditTarget}
    <AddDialogueLogModal
      open={true}
      mode="edit"
      initial={entryEditTarget}
      {firmId}
      lawyers={firmLawyers}
      proposals={proposalOptions}
      onClose={() => (entryEditTarget = null)}
      onUpdated={handleEntryUpdated}
    />
  {/if}
  {#if firmId}
    <!--
      関心弁護士の初期値は通常の「+ 契約を追加」のみ。 結果タブ (営業アクション) 起点の
      作成では渡さない — 閲覧中の弁護士はその行の対象弁護士でもあるため、 渡すと
      「対象弁護士を関心弁護士へ自動解釈しない」契約 (86eya0dva) に反する。
      対象弁護士の引継ぎ先は initialRelatedLawyerIds だけ。
    -->
    <ContractCreateModal
      open={contractCreateOpen}
      fixedFirmId={firmId}
      {opportunities}
      initialMainInterestLawyerId={contractLinkFlow.sourceAction
        ? undefined
        : lawyer.id}
      initialRelatedLawyerIds={contractLinkFlow.sourceAction
        ?.participant_lawyer_ids ?? []}
      LineItemsEditor={ContractLineItemsEditor}
      lineItemsCanEdit={allowEdit}
      onClose={() => {
        contractCreateOpen = false;
        contractLinkFlow.clearSource();
      }}
      onCreated={handleContractCreated}
    />
  {/if}
  {#if firmId && contractEditTarget}
    <ContractCreateModal
      open={true}
      mode="edit"
      initial={contractEditTarget}
      fixedFirmId={firmId}
      {opportunities}
      LineItemsEditor={ContractLineItemsEditor}
      lineItemsCanEdit={allowEdit}
      onClose={() => {
        contractEditTarget = null;
      }}
      onUpdated={handleContractUpdated}
    />
  {/if}
  {#if contractTransitionTarget}
    <ContractTransitionModal
      open={true}
      contract={contractTransitionTarget}
      onClose={() => (contractTransitionTarget = null)}
      onChanged={handleContractTransitioned}
    />
  {/if}
  {#if contractArchiveTarget}
    <ContractArchiveModal
      open={true}
      contract={contractArchiveTarget}
      onClose={() => (contractArchiveTarget = null)}
      onArchived={handleContractArchived}
    />
  {/if}
  {#if contractRestoreTarget}
    <ContractRestoreModal
      open={true}
      contract={contractRestoreTarget}
      onClose={() => (contractRestoreTarget = null)}
      onRestored={handleContractRestored}
    />
  {/if}
{/if}

<style>
  .back-link {
    margin: 0 0 8px;
  }
  .back-link a {
    color: #2563eb;
    text-decoration: none;
    font-size: 0.875rem;
  }
  .back-link a:hover {
    text-decoration: underline;
  }
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 24px;
    margin: 0 0 16px;
    padding: 16px;
    background: #fff;
    border: 1px solid #dfe3ea;
    border-radius: 8px;
  }
  .header-main {
    flex: 1 1 360px;
    min-width: 0;
  }
  h1 {
    margin: 0;
    font-size: 22px;
  }
  .meta {
    margin: 8px 0 0;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    color: #4b5563;
    font-size: 0.875rem;
  }
  .meta a {
    color: #1d4ed8;
  }
  .sales-caution-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
  }
  .sales-caution-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
    white-space: nowrap;
  }
  .sales-caution-badge-excluded {
    background: #fee2e2;
    color: #991b1b;
  }
  .sales-caution-badge-competitor {
    background: #fff7ed;
    color: #9a3412;
  }
  .sales-caution-badge-disciplinary-review {
    background: #fef3c7;
    color: #92400e;
    box-shadow: inset 0 0 0 1px #fbbf24;
  }
  .sales-caution-badge-disciplinary-stop {
    background: #fff1f2;
    color: #9f1239;
    box-shadow: inset 0 0 0 1px #fda4af;
  }
  .reason-missing {
    display: inline-grid;
    place-items: center;
    width: 14px;
    height: 14px;
    border-radius: 999px;
    background: rgba(185, 28, 28, 0.14);
    color: #991b1b;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
  }
  .sales-caution-alerts {
    display: grid;
    gap: 4px;
    margin-top: 8px;
    padding: 8px 10px;
    border: 1px solid #fecaca;
    border-radius: 6px;
    background: #fff7f7;
    color: #7f1d1d;
    font-size: 13px;
  }
  .sales-caution-alerts p {
    margin: 0;
  }
  .pending-form-alert {
    display: grid;
    gap: 8px;
    margin: 0 0 12px;
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 6px;
    color: #92400e;
    font-size: 13px;
  }
  .pending-form-alert-head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-weight: 700;
  }
  .pending-form-alert-head strong {
    font-size: 12px;
  }
  .pending-form-links {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .pending-form-links a {
    color: #1d4ed8;
    font-weight: 700;
  }
  .pending-form-error {
    margin: 0;
    color: #7f1d1d;
  }
  /* 右上は 担当者 (1 行) + 更新日 (1 行) を表示。 */
  .title-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: 0 0 auto;
    max-width: 100%;
    gap: 8px;
  }
  .assignee {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
    color: #374151;
  }
  .assignee-label {
    color: #6b7280;
    font-weight: 700;
    white-space: nowrap;
  }
  .assignee-value {
    color: #111827;
  }
  .assignee-saving {
    color: #6b7280;
    white-space: nowrap;
  }
  .assignee-error {
    color: #b91c1c;
    max-width: 220px;
    white-space: normal;
    text-align: right;
  }
  .assignee :global(select) {
    max-width: 220px;
    font-size: 12px;
    padding: 4px 8px;
  }
  .meta-update {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .tabs {
    display: flex;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 16px;
    gap: 4px;
    flex-wrap: wrap;
  }
  .tabs button {
    border: 0;
    border-radius: 0;
    padding: 8px 16px;
    background: transparent;
    color: #6b7280;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
  }
  .tabs button.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
  .detail-profile-grid {
    display: grid;
    grid-template-columns: minmax(420px, 4.5fr) minmax(0, 5.5fr);
    gap: 16px;
    align-items: start;
  }
  .profile-summary,
  .profile-timeline {
    min-width: 0;
  }
  .sales-action-stack {
    display: grid;
    gap: 16px;
  }
  .timeline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .timeline-header h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .timeline-actions {
    display: flex;
    gap: 8px;
  }
  .timeline-actions button {
    padding: 6px 12px;
    font-size: 0.875rem;
  }
  .status {
    color: #6b7280;
  }
  .status.error {
    color: #b91c1c;
  }
  .tab-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .tab-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .tab-header h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .tab-header button {
    padding: 6px 12px;
    font-size: 0.875rem;
  }
  .tab-header button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  .link-error {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 8px 10px;
    border: 1px solid #fecaca;
    border-radius: 6px;
    background: #fff7f7;
  }
  .link-error p {
    margin: 0;
  }
  .contract-view-toggle {
    display: inline-flex;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    overflow: hidden;
    width: fit-content;
  }
  .contract-view-toggle button {
    padding: 6px 14px;
    border: 0;
    background: white;
    color: #4b5563;
    cursor: pointer;
    font: inherit;
    font-size: 0.8125rem;
  }
  .contract-view-toggle button + button {
    border-left: 1px solid #d1d5db;
  }
  .contract-view-toggle button.active {
    background: #2563eb;
    color: white;
  }

  @media (max-width: 1024px) {
    .detail-profile-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
